/**
 * card-ops — block-level source edits for the card editor, layered on the pure
 * line helpers in `ls/edit-source`. These translate a card action (insert a
 * templated block after card N, delete card N, move top-level card A before
 * card B) into a concrete new source string.
 *
 * Indentation policy: an inserted block inherits the indentation of the block it
 * is inserted after (its sibling), so a beat added inside a `@choice` body lines
 * up with the other options. When inserting at the very top (no anchor), the
 * template keeps its own zero indentation.
 *
 * Reorder scope: only TOP-LEVEL cards (depth 0) participate in drag reorder.
 * Moving a compound block across another compound block's interior is ambiguous
 * (there is no unambiguous "between two options" target at depth 0), so drop
 * targets are restricted to same-depth (depth-0) siblings — see `moveTopLevelBlock`.
 */
import { parseLsBlocks, type LsBlock } from "../../ls/parse-blocks.js";
import { blockLineRange, deleteLines, insertLine, moveLines, type LineRange } from "../../ls/edit-source.js";
import { defaultChoiceRouteTemplate, templateFor, type InsertKind } from "./block-templates.js";

export interface GateRouteConnection {
  readonly optionId: string;
  readonly result?: "success" | "fail" | "any";
  readonly target: {
    readonly kind: "next" | "end";
    readonly value: string;
  };
}

export interface GateRouteConnectionResult {
  readonly source: string;
  readonly line: number | null;
  readonly changed: boolean;
}

export interface InsertBlockResult {
  readonly source: string;
  readonly insertedLine: number | null;
}

export interface MoveBlockResult {
  readonly source: string;
  readonly movedLine: number | null;
  readonly changed: boolean;
}

export interface DeleteBlockResult {
  readonly source: string;
  readonly deletedLine: number | null;
  readonly changed: boolean;
}

export const CANVAS_BLOCK_MARKER = "// @canvas-block";

/** The leading-whitespace prefix of a 1-based source line (spaces or tabs). */
function indentOfLine(source: string, lineNo: number): string {
  const lines = source.split(/\r?\n/);
  const line = lines[lineNo - 1] ?? "";
  return line.match(/^\s*/)?.[0] ?? "";
}

/** Re-indent every line of a (possibly multi-line) template by `indent`. */
function reindent(template: string, indent: string): string {
  if (indent === "") return template;
  return template
    .split("\n")
    .map((l) => (l === "" ? l : indent + l))
    .join("\n");
}

/** Return the last line that looks like a standalone closing brace. */
function lastStandaloneCloseLine(source: string): number | undefined {
  const lines = source.split(/\r?\n/);
  for (let i = lines.length - 1; i >= 0; i--) {
    if ((lines[i] ?? "").trim() === "}") return i + 1;
  }
  return undefined;
}

function hasAncestorKind(blocks: readonly LsBlock[], index: number, kind: LsBlock["kind"]): boolean {
  const block = blocks[index];
  if (!block) return false;
  for (let i = index - 1; i >= 0; i--) {
    const candidate = blocks[i]!;
    if (candidate.depth >= block.depth) continue;
    if (candidate.kind === kind) return true;
  }
  return false;
}

function hasGate(blocks: readonly LsBlock[]): boolean {
  return blocks.some((b) => b.kind === "gate");
}

function hasEpisode(blocks: readonly LsBlock[]): boolean {
  return blocks.some((b) => b.kind === "episode");
}

function lineAt(source: string, lineNo: number): string {
  return source.split(/\r?\n/)[lineNo - 1] ?? "";
}

function rangesOverlap(a: LineRange, b: LineRange): boolean {
  return a.start <= b.end && b.start <= a.end;
}

function reindentLines(lines: readonly string[], targetIndent: string): string[] {
  const baseIndent =
    lines.find((line) => line.trim() !== "")?.match(/^\s*/)?.[0] ?? "";
  return lines.map((line) => {
    if (line.trim() === "") return line;
    if (baseIndent && line.startsWith(baseIndent)) return `${targetIndent}${line.slice(baseIndent.length)}`;
    return `${targetIndent}${line.trimStart()}`;
  });
}

function replaceLine(source: string, lineNo: number, text: string): string {
  return insertLine(deleteLines(source, lineNo, lineNo), lineNo, text);
}

function routeOutcomeText(target: GateRouteConnection["target"]): string {
  return target.kind === "next" ? `@next ${target.value}` : `@end ${target.value}`;
}

function isValidRouteTarget(target: GateRouteConnection["target"]): boolean {
  const value = target.value.trim();
  if (target.kind === "next") return /^[A-Za-z0-9_./:-]+$/.test(value);
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(value);
}

function isRouteLine(block: LsBlock): block is Extract<LsBlock, { kind: "if" }> {
  return block.kind === "if" && (Boolean(block.next) || Boolean(block.end));
}

function routeReferences(condition: string | undefined, optionId: string, result: "success" | "fail" | "any"): boolean {
  return condition?.trim() === `${optionId}.${result}`;
}

function exactChoiceRouteCondition(condition: string | undefined, optionIds: ReadonlySet<string>): boolean {
  if (!condition) return false;
  const match = condition.trim().match(/^([A-Za-z][A-Za-z0-9_-]*)\.(success|fail|any)$/);
  return Boolean(match && optionIds.has(match[1]!));
}

function replaceRouteOutcome(line: string, target: GateRouteConnection["target"]): string {
  const outcome = routeOutcomeText(target);
  if (/@next\s+\S+/.test(line)) return line.replace(/@next\s+\S+/, outcome);
  if (/@end\s+\S+/.test(line)) return line.replace(/@end\s+\S+/, outcome);
  return `${line.replace(/\s*$/, "")}: ${outcome}`;
}

function gateBodyIndent(source: string, gateLine: number): string {
  return `${indentOfLine(source, gateLine)}  `;
}

function gateInsertLine(
  condition: string,
  target: GateRouteConnection["target"],
  useElseIf: boolean,
  indent: string,
): string {
  const head = useElseIf ? "@else @if" : "@if";
  return `${indent}${head} (${condition}): ${routeOutcomeText(target)}`;
}

function appendDefaultChoiceRoute(source: string, blocksBeforeInsert: readonly LsBlock[]): string {
  const routeTemplate = defaultChoiceRouteTemplate();
  if (hasEpisode(blocksBeforeInsert)) {
    const closeLine = lastStandaloneCloseLine(source);
    if (closeLine !== undefined) {
      return insertLine(source, closeLine, reindent(routeTemplate, `${indentOfLine(source, closeLine)}  `));
    }
  }

  const lineCount = source === "" ? 0 : source.split(/\r?\n/).length;
  return insertLine(source, lineCount + 1, routeTemplate);
}

function maybeAppendDefaultChoiceRoute(
  source: string,
  blocksBeforeInsert: readonly LsBlock[],
  kind: InsertKind,
): string {
  if (kind !== "choice" || hasGate(blocksBeforeInsert)) return source;
  return appendDefaultChoiceRoute(source, blocksBeforeInsert);
}

function parentChoiceIndex(blocks: readonly LsBlock[], index: number): number {
  const block = blocks[index];
  if (!block) return -1;
  for (let i = index - 1; i >= 0; i--) {
    const candidate = blocks[i]!;
    if (candidate.depth >= block.depth) continue;
    if (candidate.kind === "choice") return i;
    return -1;
  }
  return -1;
}

function optionsInsideRange(blocks: readonly LsBlock[], range: LineRange): Extract<LsBlock, { kind: "option" }>[] {
  return blocks.filter(
    (block): block is Extract<LsBlock, { kind: "option" }> =>
      block.kind === "option" && block.line >= range.start && block.line <= range.end,
  );
}

function choiceOptionCount(source: string, blocks: readonly LsBlock[], choiceIndex: number): number {
  const choice = blocks[choiceIndex];
  if (!choice || choice.kind !== "choice") return 0;
  const range = blockLineRange(source, blocks, choiceIndex);
  return optionsInsideRange(blocks, range).filter((option) => option.depth === choice.depth + 1).length;
}

function cleanupExactChoiceRoutes(source: string, optionIds: ReadonlySet<string>): string {
  if (optionIds.size === 0) return source;
  let next = source;
  const blocks = parseLsBlocks(next);
  const routeLines = blocks
    .filter(
      (block): block is Extract<LsBlock, { kind: "if" }> =>
        block.kind === "if" &&
        (Boolean(block.next) || Boolean(block.end)) &&
        exactChoiceRouteCondition(block.condition, optionIds),
    )
    .map((block) => block.line)
    .sort((a, b) => b - a);
  for (const line of routeLines) {
    next = deleteLines(next, line, line);
  }
  return normalizeGateRouteHeads(next);
}

function normalizeGateRouteHeads(source: string): string {
  let next = source;
  const blocks = parseLsBlocks(next);
  const gates = blocks
    .map((block, index) => ({ block, index }))
    .filter((item): item is { block: Extract<LsBlock, { kind: "gate" }>; index: number } => item.block.kind === "gate");

  for (const gate of gates) {
    const range = blockLineRange(next, blocks, gate.index);
    const routeBlocks = blocks.filter(
      (block): block is Extract<LsBlock, { kind: "if" }> =>
        block.kind === "if" && block.line > range.start && block.line < range.end && (Boolean(block.next) || Boolean(block.end)),
    );
    const firstConditional = routeBlocks.find((block) => Boolean(block.condition));
    if (!firstConditional?.isElse) continue;
    const oldLine = lineAt(next, firstConditional.line);
    const newLine = oldLine.replace(/^(\s*)@else\s+@if\b/, "$1@if");
    if (newLine !== oldLine) next = replaceLine(next, firstConditional.line, newLine);
  }
  return next;
}

/**
 * Wire a canvas option connection into the tail `@gate` route table.
 *
 * This keeps `.ls` as the only source of truth: connecting option A to a target
 * does not create graph metadata, it either updates `A.any` (or success/fail)
 * when that route already exists, or inserts a new ordered gate condition before
 * the fallback route. Inline gates are expanded to the multi-line table form so
 * future route edits remain readable in both card and code mode.
 */
export function connectChoiceOptionToRoute(
  source: string,
  blocks: readonly LsBlock[],
  connection: GateRouteConnection,
): GateRouteConnectionResult {
  const optionId = connection.optionId.trim();
  const targetValue = connection.target.value.trim();
  if (!optionId || !targetValue) return { source, line: null, changed: false };
  if (!isValidRouteTarget({ kind: connection.target.kind, value: targetValue })) return { source, line: null, changed: false };

  const result = connection.result ?? "any";
  const condition = `${optionId}.${result}`;
  const gateBlocks = blocks
    .map((block, index) => ({ block, index }))
    .filter((item): item is { block: Extract<LsBlock, { kind: "gate" }>; index: number } => item.block.kind === "gate");
  const gateItem = gateBlocks[gateBlocks.length - 1];

  if (!gateItem) {
    const closeLine = lastStandaloneCloseLine(source);
    const gateIndent = closeLine !== undefined && hasEpisode(blocks) ? `${indentOfLine(source, closeLine)}  ` : "";
    const routeIndent = `${gateIndent}  `;
    const template = [
      `${gateIndent}@gate {`,
      gateInsertLine(condition, { kind: connection.target.kind, value: targetValue }, false, routeIndent),
      `${routeIndent}@else: @end to_be_continued`,
      `${gateIndent}}`,
    ].join("\n");
    const line = closeLine ?? (source === "" ? 1 : source.split(/\r?\n/).length + 1);
    return { source: insertLine(source, line, template), line: line + 1, changed: true };
  }

  const gate = gateItem.block;
  const gateRange = blockLineRange(source, blocks, gateItem.index);
  const target = { kind: connection.target.kind, value: targetValue };

  if (gate.raw.includes("}")) {
    const gateIndent = indentOfLine(source, gate.line);
    const routeIndent = `${gateIndent}  `;
    const fallback = gate.next
      ? `${routeIndent}@else: @next ${gate.next}`
      : gate.end
        ? `${routeIndent}@else: @end ${gate.end}`
        : `${routeIndent}@else: @end to_be_continued`;
    const expanded = [
      `${gateIndent}@gate {`,
      gateInsertLine(condition, target, false, routeIndent),
      fallback,
      `${gateIndent}}`,
    ].join("\n");
    return { source: replaceLine(source, gate.line, expanded), line: gate.line + 1, changed: true };
  }

  const routeBlocks = blocks.filter(
    (block): block is Extract<LsBlock, { kind: "if" }> =>
      block.line > gateRange.start && block.line < gateRange.end && isRouteLine(block),
  );
  const existing = routeBlocks.find((block) => routeReferences(block.condition, optionId, result));
  if (existing) {
    const oldLine = lineAt(source, existing.line);
    const nextLine = replaceRouteOutcome(oldLine, target);
    if (nextLine === oldLine) return { source, line: existing.line, changed: false };
    return { source: replaceLine(source, existing.line, nextLine), line: existing.line, changed: true };
  }

  const fallback = routeBlocks.find((block) => block.isElse && !block.condition);
  const insertAt = fallback?.line ?? gateRange.end;
  const hasEarlierConditional = routeBlocks.some((block) => !block.isElse || Boolean(block.condition));
  const routeLine = gateInsertLine(condition, target, hasEarlierConditional, gateBodyIndent(source, gate.line));
  return { source: insertLine(source, insertAt, routeLine), line: insertAt, changed: true };
}

/**
 * Insert a templated block of `kind` immediately AFTER the block at `afterIndex`
 * (its full line-range, so it lands after a whole compound block, not inside
 * it). Pass `afterIndex = -1` (or an empty document) to insert at the very top.
 * The inserted lines inherit the anchor block's indentation.
 */
export function insertBlockAfterWithResult(
  source: string,
  blocks: readonly LsBlock[],
  afterIndex: number,
  kind: InsertKind,
): InsertBlockResult {
  const template = templateFor(kind);

  // Top insert (no anchor / empty doc): prepend, keep zero indent.
  if (afterIndex < 0 || blocks.length === 0) {
    const next = source === "" ? template : insertLine(source, 1, template);
    return { source: maybeAppendDefaultChoiceRoute(next, blocks, kind), insertedLine: 1 };
  }

  const idx = Math.min(afterIndex, blocks.length - 1);
  const range = blockLineRange(source, blocks, idx);
  const indent = indentOfLine(source, range.start);
  const indented = reindent(template, indent);
  // Insert on the line right after the anchor block's last line.
  const next = insertLine(source, range.end + 1, indented);
  return { source: maybeAppendDefaultChoiceRoute(next, blocks, kind), insertedLine: range.end + 1 };
}

export function insertBlockAfter(
  source: string,
  blocks: readonly LsBlock[],
  afterIndex: number,
  kind: InsertKind,
): string {
  return insertBlockAfterWithResult(source, blocks, afterIndex, kind).source;
}

export function insertBlockAfterLineWithResult(
  source: string,
  blocks: readonly LsBlock[],
  afterLine: number,
  kind: InsertKind,
): InsertBlockResult {
  const index = blocks.findIndex((block) => block.line === afterLine);
  if (index < 0) return insertBlockNearLineWithResult(source, blocks, kind, afterLine);
  return insertBlockAfterWithResult(source, blocks, index, kind);
}

/**
 * Insert a templated block at the writer-friendly tail of an episode. For a
 * normal episode this means BEFORE the `@gate`, because `@gate` must stay last.
 * For an episode without a route yet, it lands before the final episode `}`.
 */
export function insertBlockBeforeRouteWithResult(
  source: string,
  blocks: readonly LsBlock[],
  kind: InsertKind,
): InsertBlockResult {
  if (blocks.length === 0) return insertBlockAfterWithResult(source, blocks, -1, kind);

  const template = templateFor(kind);
  const gate = blocks.find((b) => b.kind === "gate");
  if (gate) {
    return {
      source: insertLine(source, gate.line, reindent(template, indentOfLine(source, gate.line))),
      insertedLine: gate.line,
    };
  }

  const closeLine = lastStandaloneCloseLine(source);
  if (closeLine !== undefined && blocks.some((b) => b.kind === "episode")) {
    const next = insertLine(source, closeLine, reindent(template, `${indentOfLine(source, closeLine)}  `));
    return { source: maybeAppendDefaultChoiceRoute(next, blocks, kind), insertedLine: closeLine };
  }

  return insertBlockAfterWithResult(source, blocks, blocks.length - 1, kind);
}

export function insertBlockBeforeRoute(
  source: string,
  blocks: readonly LsBlock[],
  kind: InsertKind,
): string {
  return insertBlockBeforeRouteWithResult(source, blocks, kind).source;
}

/**
 * Canvas insertion: when a visual/source node is selected, insert after that
 * exact block. If no selected line is available — or the selected block is the
 * route block that must remain last — fall back to the safe episode tail.
 */
export function insertBlockNearLineWithResult(
  source: string,
  blocks: readonly LsBlock[],
  kind: InsertKind,
  afterLine?: number | null,
): InsertBlockResult {
  if (kind === "gate") return insertBlockBeforeRouteWithResult(source, blocks, kind);

  if (afterLine != null) {
    const index = blocks.findIndex((b) => b.line === afterLine);
    const block = blocks[index];
    if (index >= 0 && block && block.kind !== "gate" && !hasAncestorKind(blocks, index, "gate")) {
      return insertBlockAfterWithResult(source, blocks, index, kind);
    }
  }
  return insertBlockBeforeRouteWithResult(source, blocks, kind);
}

/**
 * Canvas insertion keeps ordinary prose/media additions visually independent.
 * The marker is a source comment, so the compiler/parser ignore it, while the
 * canvas can use it as an explicit split point instead of forcing authors to
 * add a fake @pause just to get a separate block.
 */
export function insertCanvasBlockNearLineWithResult(
  source: string,
  blocks: readonly LsBlock[],
  kind: InsertKind,
  afterLine?: number | null,
): InsertBlockResult {
  if (kind !== "gate" && afterLine != null) {
    const index = blocks.findIndex((block) => block.line === afterLine);
    const block = blocks[index];
    if (block?.kind === "option") {
      return insertBlockInsideOptionWithResult(source, blocks, afterLine, kind);
    }
  }

  const result = insertBlockNearLineWithResult(source, blocks, kind, afterLine);
  if (result.insertedLine == null || result.source === source) return result;

  const lines = result.source.split(/\r?\n/);
  const inserted = lines[result.insertedLine - 1] ?? "";
  const marker = `${inserted.match(/^\s*/)?.[0] ?? ""}${CANVAS_BLOCK_MARKER}`;
  return {
    source: insertLine(result.source, result.insertedLine, marker),
    insertedLine: result.insertedLine + 1,
  };
}

export function splitCanvasBlockBeforeLineWithResult(
  source: string,
  line: number | null | undefined,
): InsertBlockResult {
  if (line == null || line <= 1) return { source, insertedLine: null };
  const lines = source.split(/\r?\n/);
  const target = lines[line - 1];
  if (target == null || target.trim() === "" || target.trim() === "}") return { source, insertedLine: null };
  const previous = lines[line - 2];
  if (previous?.trim() === CANVAS_BLOCK_MARKER) return { source, insertedLine: line };
  const marker = `${target.match(/^\s*/)?.[0] ?? ""}${CANVAS_BLOCK_MARKER}`;
  return { source: insertLine(source, line, marker), insertedLine: line + 1 };
}

export function insertBlockInsideOptionWithResult(
  source: string,
  blocks: readonly LsBlock[],
  optionLine: number | null | undefined,
  kind: InsertKind,
): InsertBlockResult {
  if (optionLine == null) return insertBlockNearLineWithResult(source, blocks, kind, optionLine);
  const optionIndex = blocks.findIndex((block) => block.line === optionLine);
  const option = blocks[optionIndex];
  if (!option || option.kind !== "option") return insertBlockNearLineWithResult(source, blocks, kind, optionLine);

  const optionRange = blockLineRange(source, blocks, optionIndex);
  const targetLine = optionRange.end;
  const indent = `${indentOfLine(source, option.line)}  `;
  const template = reindent(templateFor(kind), indent);
  const withMarker = insertLine(source, targetLine, `${indent}${CANVAS_BLOCK_MARKER}`);
  return {
    source: insertLine(withMarker, targetLine + 1, template),
    insertedLine: targetLine + 1,
  };
}

export function insertBlockNearLine(
  source: string,
  blocks: readonly LsBlock[],
  kind: InsertKind,
  afterLine?: number | null,
): string {
  return insertBlockNearLineWithResult(source, blocks, kind, afterLine).source;
}

/** Delete the block at `index` (its full line-range) from the source. */
export function deleteBlockAt(source: string, blocks: readonly LsBlock[], index: number): string {
  if (index < 0 || index >= blocks.length) return source;
  const range = blockLineRange(source, blocks, index);
  return deleteLines(source, range.start, range.end);
}

export function deleteBlockByLineWithResult(
  source: string,
  blocks: readonly LsBlock[],
  line: number | null | undefined,
): DeleteBlockResult {
  if (line == null) return { source, deletedLine: null, changed: false };
  let index = blocks.findIndex((block) => block.line === line);
  let block = blocks[index];
  if (!block || block.kind === "episode") return { source, deletedLine: null, changed: false };

  if (block.kind === "option") {
    const choiceIndex = parentChoiceIndex(blocks, index);
    if (choiceIndex >= 0 && choiceOptionCount(source, blocks, choiceIndex) <= 1) {
      index = choiceIndex;
      block = blocks[index];
    }
  }

  const range = blockLineRange(source, blocks, index);
  const optionIds = new Set(
    optionsInsideRange(blocks, range)
      .map((option) => option.id)
      .filter((id): id is string => Boolean(id)),
  );
  const deleted = deleteBlockAt(source, blocks, index);
  const next = cleanupExactChoiceRoutes(deleted, optionIds);
  return { source: next, deletedLine: block?.line ?? null, changed: next !== source };
}

/**
 * The indices (into `blocks`) of the TOP-LEVEL (depth 0) cards, in document
 * order. These are the only cards that participate in drag reorder.
 */
export function topLevelIndices(blocks: readonly LsBlock[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < blocks.length; i++) {
    if (blocks[i]!.depth === 0) out.push(i);
  }
  return out;
}

/**
 * Move a TOP-LEVEL card so it lands at the position currently held by another
 * top-level card. `fromIndex` / `toIndex` are indices into `blocks`; both MUST
 * be depth-0 (the drag UI only exposes depth-0 handles). The moved block's whole
 * line-range travels as a unit, so compound blocks never split.
 *
 * Limitation: reorder is restricted to depth-0 siblings. Dropping a compound
 * block *inside* another compound block's body is not supported here — there is
 * no unambiguous depth-0 target for an interior slot, and doing so line-wise
 * would risk unbalancing the host block. Nested reordering stays a code-mode
 * task. (See module header.)
 */
export function moveTopLevelBlock(
  source: string,
  blocks: readonly LsBlock[],
  fromIndex: number,
  toIndex: number,
): string {
  const from = blocks[fromIndex];
  const to = blocks[toIndex];
  if (!from || !to) return source;
  // Guard: only same-depth (depth 0) moves are supported.
  if (from.depth !== 0 || to.depth !== 0) return source;
  if (fromIndex === toIndex) return source;

  const fromRange = blockLineRange(source, blocks, fromIndex);
  const toRange = blockLineRange(source, blocks, toIndex);

  // Target line: when moving DOWN (from before to), drop the chunk AFTER the
  // target block (its range end + 1) so it lands past the target — this matches
  // dnd-kit's "reorder past" semantics. When moving UP, drop before the target's
  // first line.
  const target = fromIndex < toIndex ? toRange.end + 1 : toRange.start;
  return moveLines(source, fromRange, target);
}

/**
 * Move an arbitrary brace-safe source span before or after another source span.
 *
 * The canvas uses this for episode visual blocks, whose depth is usually inside
 * the `@episode` body rather than top-level. Callers must only pass complete
 * block ranges; this helper deliberately knows nothing about parsed block kinds.
 */
export function moveLineRangeRelative(
  source: string,
  fromRange: LineRange,
  targetRange: LineRange,
  placement: "before" | "after",
): string {
  if (fromRange.start === targetRange.start && fromRange.end === targetRange.end) return source;
  const targetLine = placement === "after" ? targetRange.end + 1 : targetRange.start;
  return moveLines(source, fromRange, targetLine);
}

export function moveLineRangeIntoOptionWithResult(
  source: string,
  blocks: readonly LsBlock[],
  fromRange: LineRange,
  optionLine: number,
): MoveBlockResult {
  const optionIndex = blocks.findIndex((block) => block.line === optionLine);
  const option = blocks[optionIndex];
  if (!option || option.kind !== "option") return { source, movedLine: null, changed: false };

  const optionRange = blockLineRange(source, blocks, optionIndex);
  if (optionRange.start === optionRange.end) return { source, movedLine: null, changed: false };
  if (rangesOverlap(fromRange, optionRange)) return { source, movedLine: null, changed: false };

  const lines = source.split(/\r?\n/);
  const moving = lines.slice(fromRange.start - 1, fromRange.end);
  if (moving.length === 0) return { source, movedLine: null, changed: false };

  const targetIndent = `${indentOfLine(source, option.line)}  `;
  const reindented = reindentLines(moving, targetIndent);
  const remaining = [
    ...lines.slice(0, fromRange.start - 1),
    ...lines.slice(fromRange.end),
  ];
  let insertIndex = optionRange.end - 1;
  if (fromRange.start < optionRange.end) insertIndex -= moving.length;
  if (insertIndex < 0 || insertIndex > remaining.length) return { source, movedLine: null, changed: false };

  remaining.splice(insertIndex, 0, ...reindented);
  const next = remaining.join("\n");
  const movedLine = insertIndex + 1;
  return { source: next, movedLine, changed: next !== source };
}

function nearestParentKey(blocks: readonly LsBlock[], index: number): string {
  const block = blocks[index];
  if (!block) return "none";
  for (let i = index - 1; i >= 0; i--) {
    const candidate = blocks[i]!;
    if (candidate.depth >= block.depth) continue;
    return `${candidate.kind}:${candidate.line}:${candidate.depth}`;
  }
  return "root";
}

export function moveSiblingBlockByLine(
  source: string,
  blocks: readonly LsBlock[],
  fromLine: number,
  toLine: number,
  placement: "before" | "after",
): MoveBlockResult {
  const fromIndex = blocks.findIndex((block) => block.line === fromLine);
  const toIndex = blocks.findIndex((block) => block.line === toLine);
  const from = blocks[fromIndex];
  const to = blocks[toIndex];
  if (!from || !to || from.line === to.line) return { source, movedLine: null, changed: false };
  if (from.kind === "episode" || to.kind === "episode") return { source, movedLine: null, changed: false };
  if (from.depth !== to.depth) return { source, movedLine: null, changed: false };
  if (nearestParentKey(blocks, fromIndex) !== nearestParentKey(blocks, toIndex)) {
    return { source, movedLine: null, changed: false };
  }
  if (hasAncestorKind(blocks, fromIndex, "gate") || hasAncestorKind(blocks, toIndex, "gate")) {
    return { source, movedLine: null, changed: false };
  }

  const next = moveLineRangeRelative(
    source,
    blockLineRange(source, blocks, fromIndex),
    blockLineRange(source, blocks, toIndex),
    placement,
  );
  return { source: next, movedLine: from.line, changed: next !== source };
}
